import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Send, Phone, MessageSquare, ShieldAlert, Sparkles, Copy, RotateCcw, ThumbsUp, ThumbsDown } from 'lucide-react';
import VoiceInputButton from '@/components/VoiceInputButton';
import { http } from '@/lib/api';
import LegalAckModal from '@/components/LegalAckModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_START_PROMPTS = [
  "My husband won't talk to me anymore.",
  'How do I encourage therapy without pushing?',
  'My son seems withdrawn lately.',
  'How do I support someone after losing a loved one?',
  "I'm worried about depression.",
  'How do I talk about suicide safely?',
  "He keeps saying he's \u201cfine.\u201d",
  'I think he\u2019s burned out.',
  "I'm exhausted from always being the helper.",
  'How do I set healthy boundaries?',
];

const FOLLOWUP_POOL = [
  'What should I say next?',
  'How do I avoid making things worse?',
  'When should I encourage therapy?',
  'How do I bring this up without him shutting down?',
  'What if he refuses to talk?',
  'How do I take care of myself through this?',
  'What signs mean I need professional help now?',
  'How do I set a healthy boundary here?',
];

function pickFollowUps(seed) {
  // Deterministic-ish rotation so follow-ups feel varied but stable per message.
  const idx = (seed * 3) % FOLLOWUP_POOL.length;
  return [FOLLOWUP_POOL[idx], FOLLOWUP_POOL[(idx + 3) % FOLLOWUP_POOL.length], FOLLOWUP_POOL[(idx + 5) % FOLLOWUP_POOL.length]];
}

function formatTime(d) {
  try {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default function SupportCoach() {
  const [messages, setMessages] = useState([]); // { role, content, ts, feedback? }
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [lastPrompt, setLastPrompt] = useState(null);
  const bottomRef = useRef(null);
  const chatBoxRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const promptSentRef = useRef(false);

  // Auto-start a role-play when arriving from a lesson's "Practice with your Coach" step.
  useEffect(() => {
    const p = searchParams.get('prompt');
    if (p && !promptSentRef.current) {
      promptSentRef.current = true;
      setSearchParams({}, { replace: true });
      send(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    // Auto-scroll to bottom on new message
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTo({ top: chatBoxRef.current.scrollHeight, behavior: 'smooth' });
    }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const runSend = async (text, { retry = false } = {}) => {
    if (!text || streaming) return;
    if (!retry) setInput('');
    setLastPrompt(text);
    // Append user + placeholder assistant
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text, ts: new Date() },
      { role: 'assistant', content: '', ts: new Date() },
    ]);
    setStreaming(true);
    try {
      const res = await fetch(`${API_URL}/api/support/coach/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        if (res.status === 402 && errBody.detail) {
          // Daily cap reached — show the warm message in the assistant bubble.
          setMessages((prev) => {
            const c = [...prev];
            c[c.length - 1] = { role: 'assistant', content: errBody.detail, ts: c[c.length - 1].ts };
            return c;
          });
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      if (!res.body) throw new Error('No stream');
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let full = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const chunk of parts) {
          if (!chunk.startsWith('data: ')) continue;
          const data = chunk.slice(6);
          if (data === '[DONE]') continue;
          if (data.startsWith('[SESSION:')) {
            setSessionId(data.replace('[SESSION:', '').replace(']', ''));
            continue;
          }
          const nudgeMatch = data.match(/^\[NUDGE:(\d+)\]$/);
          if (nudgeMatch) { const n = nudgeMatch[1]; toast(`${n} more Coach message${n === '1' ? '' : 's'} today — we can go a bit more.`); continue; }
          const rendered = data.replaceAll('\\n', '\n');
          full += rendered;
          setMessages((prev) => {
            const c = [...prev];
            c[c.length - 1] = { role: 'assistant', content: full, ts: c[c.length - 1].ts };
            return c;
          });
        }
      }
      if (!full.trim()) {
        // Empty stream — treat as failure to avoid stuck loading UI
        throw new Error('Empty response');
      }
    } catch (e) {
      // Friendly, professional message. Never expose raw JSON or status codes.
      const friendly = 'The Support Coach is temporarily unavailable. Please try again in a moment — your message was not sent.';
      toast.error(friendly);
      setMessages((prev) => {
        const c = [...prev];
        // Remove empty assistant placeholder
        if (c.length && c[c.length - 1].role === 'assistant' && !c[c.length - 1].content) c.pop();
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  const send = (msg) => {
    const text = (msg || input).trim();
    return runSend(text);
  };

  const regenerate = () => {
    if (!lastPrompt || streaming) return;
    // Drop the last assistant message (if any) and re-send the last prompt
    setMessages((prev) => {
      const c = [...prev];
      if (c.length && c[c.length - 1].role === 'assistant') c.pop();
      if (c.length && c[c.length - 1].role === 'user') c.pop();
      return c;
    });
    runSend(lastPrompt, { retry: true });
  };

  const copyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };

  const rateMessage = (i, val) => {
    setMessages((prev) => {
      const c = [...prev];
      c[i] = { ...c[i], feedback: val };
      return c;
    });
    // Fire-and-forget; ignore errors (feedback is non-critical)
    http.post('/feedback', {
      category: 'other',
      message: `[SupportCoach ${val === 1 ? '👍' : '👎'}] ${(messages[i]?.content || '').slice(0, 500)}`,
      page_url: '/app/support/coach',
    }).catch(() => {});
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="flex flex-col min-h-[calc(100vh-6rem)] lg:min-h-[calc(100vh-4rem)] lg:max-w-4xl lg:mx-auto lg:w-full" data-testid="support-coach-page">
      <LegalAckModal
        storageKey="bp_ack_support_coach_v1"
        title="Before you use the Support Coach"
        body="This guidance is educational and is not a substitute for professional advice or emergency services."
        readMoreTo="/app/profile/trust-safety?section=ai-disclosure"
        readMoreLabel="Read the AI Coach Disclosure"
      />
      {/* HERO */}
      <div className="px-6 lg:px-2 pt-6 lg:pt-10 pb-3">
        <Link to="/app/support" className="inline-flex items-center gap-1 text-xs text-navy/50 hover:text-navy mb-3">
          <ArrowLeft size={12}/> Back to Support
        </Link>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Support Coach</p>
        <h1 className="font-heading text-3xl lg:text-4xl mt-1 lg:mt-2 leading-tight" data-testid="support-coach-title">Support Coach</h1>
        <p className="text-navy/70 mt-1 lg:mt-2 text-sm lg:text-base" data-testid="support-coach-subtitle">Evidence-informed guidance for the people who care.</p>
        <p className="hidden lg:block text-navy/60 mt-3 max-w-3xl text-sm leading-relaxed">
          Talk through difficult conversations, understand warning signs, learn healthy boundaries, and receive practical support for helping someone you care about.
          This coach never diagnoses, replaces therapy, or tells you to control another person.
        </p>
      </div>

      {/* EMPTY STATE + QUICK START */}
      {!hasMessages && (
        <div className="px-6 lg:px-2 pb-3 space-y-5" data-testid="support-coach-empty">
          <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(197,160,89,0.14)' }}>
                <Sparkles size={17} className="text-gold" strokeWidth={1.5}/>
              </div>
              <div>
                <p className="font-heading text-lg lg:text-xl text-navy">How can I help today?</p>
                <p className="text-sm lg:text-base text-navy/70 mt-2 leading-relaxed">
                  Describe what&apos;s happening and I&apos;ll help you think through the situation together.
                </p>
                <p className="text-sm text-navy/60 mt-3">You can ask about:</p>
                <ul className="text-sm text-navy/70 mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 leading-relaxed">
                  {['difficult conversations','supporting mental health','grief','anger','therapy','communication','boundaries','burnout','relationships','crisis planning'].map(t => (
                    <li key={t} className="flex items-start gap-1.5"><span className="text-gold mt-1 shrink-0 w-1 h-1 rounded-full bg-gold"/>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-navy/50 font-semibold mb-3">Quick start</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="support-coach-prompts">
              {QUICK_START_PROMPTS.map((p, i) => (
                <button
                  key={p}
                  data-testid={`support-prompt-${i}`}
                  onClick={() => send(p)}
                  className="text-left text-sm bg-white text-navy/85 border border-navy/10 px-4 py-3 rounded-2xl hover:border-navy/40 hover:-translate-y-0.5 transition-smooth"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* MESSAGES */}
      <div ref={chatBoxRef} className="flex-1 overflow-y-auto px-6 lg:px-2 py-4 space-y-5 lg:space-y-6">
        {messages.map((m, i) => {
          const isLastAssistant = m.role === 'assistant' && i === messages.length - 1;
          const isStreamingHere = streaming && isLastAssistant;
          return (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-[88%] lg:max-w-[78%]">
                <div
                  data-testid={`sc-msg-${m.role}-${i}`}
                  className={`rounded-3xl px-5 py-3.5 lg:px-6 lg:py-4 whitespace-pre-wrap text-sm lg:text-base leading-relaxed break-words ${
                    m.role === 'user' ? 'bg-navy text-white' : 'bg-white text-navy card-soft'
                  }`}
                >
                  {m.content || (isStreamingHere ? (
                    <span className="inline-flex items-center gap-1 text-navy/50" data-testid="typing-indicator">
                      <span className="w-1.5 h-1.5 rounded-full bg-navy/50 animate-soft-pulse"/>
                      <span className="w-1.5 h-1.5 rounded-full bg-navy/50 animate-soft-pulse" style={{ animationDelay: '0.15s' }}/>
                      <span className="w-1.5 h-1.5 rounded-full bg-navy/50 animate-soft-pulse" style={{ animationDelay: '0.3s' }}/>
                    </span>
                  ) : '')}
                </div>

                <div className="flex items-center gap-3 mt-1.5 px-1">
                  {m.ts && (
                    <span className={`text-[10px] ${m.role === 'user' ? 'text-navy/40 ml-auto' : 'text-navy/40'}`}>
                      {formatTime(new Date(m.ts))}
                    </span>
                  )}
                  {m.role === 'assistant' && m.content && !isStreamingHere && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copyMessage(m.content)}
                        data-testid={`sc-copy-${i}`}
                        className="text-navy/40 hover:text-navy transition-smooth"
                        title="Copy"
                      ><Copy size={13}/></button>
                      {i === messages.length - 1 && (
                        <button
                          onClick={regenerate}
                          disabled={streaming}
                          data-testid={`sc-regen-${i}`}
                          className="text-navy/40 hover:text-navy transition-smooth disabled:opacity-30"
                          title="Regenerate"
                        ><RotateCcw size={13}/></button>
                      )}
                      <button
                        onClick={() => rateMessage(i, 1)}
                        data-testid={`sc-up-${i}`}
                        className={`transition-smooth ${m.feedback === 1 ? 'text-gold' : 'text-navy/40 hover:text-navy'}`}
                        title="Helpful"
                      ><ThumbsUp size={13}/></button>
                      <button
                        onClick={() => rateMessage(i, -1)}
                        data-testid={`sc-down-${i}`}
                        className={`transition-smooth ${m.feedback === -1 ? 'text-red-500' : 'text-navy/40 hover:text-navy'}`}
                        title="Not helpful"
                      ><ThumbsDown size={13}/></button>
                    </div>
                  )}
                </div>

                {/* Suggested follow-ups after final assistant message */}
                {m.role === 'assistant' && m.content && !isStreamingHere && i === messages.length - 1 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`sc-followups-${i}`}>
                    {pickFollowUps(i + messages.length).map((q, idx) => (
                      <button
                        key={`${i}-${idx}-${q}`}
                        onClick={() => send(q)}
                        data-testid={`sc-followup-${i}-${idx}`}
                        className="text-xs bg-beige text-navy px-3.5 py-1.5 rounded-full hover:bg-[#EAE5D9] transition-smooth"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* CRISIS + DISCLAIMER + INPUT */}
      <div className="px-6 lg:px-2 pb-6 lg:pb-8 pt-2 bg-[#F9F8F6]">
        <div className="mb-3 flex flex-wrap items-center justify-center gap-2 text-[10px] lg:text-xs">
          <a href="tel:988" className="inline-flex items-center gap-1 bg-red-50 text-red-700 rounded-full px-3 py-1 hover:bg-red-100">
            <Phone size={10}/> Call 988
          </a>
          <a href="sms:988" className="inline-flex items-center gap-1 bg-red-50 text-red-700 rounded-full px-3 py-1 hover:bg-red-100">
            <MessageSquare size={10}/> Text 988
          </a>
          <span className="text-navy/40">·</span>
          <Link to="/app/support/sup_crisis" className="inline-flex items-center gap-1 text-navy/60 hover:text-navy">
            <ShieldAlert size={10}/> Crisis guide
          </Link>
        </div>
        <p className="text-[10px] lg:text-xs text-navy/50 mb-3 text-center max-w-2xl mx-auto leading-relaxed" data-testid="sc-disclaimer">
          Brotherhood Support provides educational guidance only. It does not diagnose mental illness, replace licensed therapy, or provide emergency intervention. If someone may be in immediate danger, call 911 or contact 988 immediately.
        </p>
        <div className="flex gap-2 items-end bg-white rounded-3xl p-2 lg:p-3 card-soft">
          <textarea
            data-testid="support-coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder="Ask about supporting a man in your life..."
            className="flex-1 resize-none px-4 py-3 bg-transparent outline-none text-sm lg:text-base max-h-40"
          />
          <VoiceInputButton
            testid="support-coach-voice-btn"
            onTranscript={(chunk) => setInput((prev) => (prev + ' ' + chunk).trim())}
          />
          <button
            data-testid="support-coach-send"
            onClick={() => send()}
            disabled={streaming || !input.trim()}
            className="w-11 h-11 lg:w-12 lg:h-12 rounded-full bg-navy text-white flex items-center justify-center disabled:opacity-40 hover:bg-navy-soft transition-smooth"
          >
            <Send size={16}/>
          </button>
        </div>
      </div>
    </div>
  );
}
